import { OrderInfo, OrderItemInfo, PaymentInfo } from "../models/models";
import { Ticket } from "../models/RestModels";
import { Utils } from "./Utils";

export type TicketCalculationStyle = 'NORMAL' | 'SAT_FACTURA';

	export interface FacturaItem {
		item_name: string;
		qty: number;
		valor_unitario: number;
		importe: number;
		descuento: number;
		rounding_error: number;
		real_discount: number;
		base: number;
		iva: number;
		ieps: number;
		tax_percent: number;
		ieps_percent: number;
		discount_percent?: number;
		has_discount: boolean;
		note?: string;
		exceptions?: string;
		batch_option?: 'NONE' | 'BATCH_ONLY' | 'EXPIRATION_ONLY' | 'BATCH_AND_EXPIRATION';
		batches: { batch?: string; expiration_date?: string }[];
	}

export interface CompactItem {
	item_name: string;
	qty: number;
	unitary_price: number;
	subtotal: number;
	total: number;
	tax: number;
}

export class TicketBuilder {

	private order_info: OrderInfo;
	private ticket: Ticket;
	private payment_info: PaymentInfo | null = null;
	private calculation_style: TicketCalculationStyle = 'NORMAL';
	private compact_items: boolean = false;
	private qty_col_width: number = 0;
	private price_col_width: number = 0;
	private total_col_width: number = 0;
	private qty2_col_width: number = 0;
	private qty_times_price_col_width: number = 0;
	private subtotal_col_width: number = 0;

	private valor_unitario_col_width: number = 0;
	private importe_col_width: number = 0;
	private descuento_col_width: number = 0;

	private show_qty_col: boolean;
	private show_price_col: boolean;
	private show_total_col: boolean;
	private show_options: boolean;
	private show_qty2_col: boolean;
	private show_qty_times_price_col: boolean;
	private show_subtotal_col: boolean;
    font_row: string = 'b';

	factura_items: FacturaItem[] = [];
	factura_subtotal: number = 0;
	factura_descuento: number = 0;
	factura_subtotal_neto: number = 0;
	factura_iva: number = 0;
	factura_ieps: number = 0;
	factura_total: number = 0;

	compact_items_list: CompactItem[] = [];

    ticket_a_columns: number = 32;
	ticket_b_columns: number = 42;

	// QR code configuration
	private facturacion_url: string = '';
	private qr_size: number = 6;

	constructor(
		order_info: OrderInfo,
		ticket: Ticket = null,
		ticket_a_columns: number = 48,
		ticket_b_columns: number = 56,
		calculation_style: TicketCalculationStyle = 'NORMAL',
		compact_items: boolean = false,
		payment_info: PaymentInfo | null = null
	)
	{
		this.order_info = order_info;
		this.ticket = ticket || this.defaulticket();
		this.calculation_style = calculation_style;
		this.compact_items = compact_items;
		this.payment_info = payment_info;

		this.show_qty_col = this.ticket.item_qty1_display !== 'NEVER';
		this.show_price_col = this.ticket.item_price_display !== 'NEVER';
		this.show_total_col = true;
		this.show_options = this.ticket.option_row_display !== 'NEVER';
		this.show_qty2_col = this.ticket.item_qty2_display !== 'NEVER';
		this.show_qty_times_price_col = this.ticket.item_qty_times_price_display !== 'NEVER';
		this.show_subtotal_col = this.ticket.item_subtotal_display !== 'NEVER';
		this.ticket_a_columns = this.ticket.font_a_columns || ticket_a_columns;
		this.ticket_b_columns = this.ticket.font_b_columns || ticket_b_columns;

		if (this.calculation_style === 'SAT_FACTURA') {
			this.calculateFacturaValues();
		}

		if (this.compact_items) {
			this.buildCompactItemsList();
		}
	}

	/**
	 * Set the facturacion URL for QR code generation
	 * @param url The base URL for facturacion (e.g., 'https://example.com/facturar')
	 * @param qrSize QR code size (default: 6)
	 */
	setFacturacionUrl(url: string, qrSize: number = 6): void {
		this.facturacion_url = url;
		this.qr_size = qrSize;
	}

	/**
	 * Escape special XML characters in a string
	 */
	private escapeXml(str: string): string {
		if (!str) return '';
		return str
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	/**
	 * Sanitize XML by adding quotes to unquoted attribute values
	 * Fixes: fontWidth=2 -> fontWidth="2"
	 */
	private sanitizeXml(xml: string): string {
		return xml.replace(/(\w+)=([^\s"'>]+)/g, '$1="$2"');
	}

	/**
	 * Check if facturacion code and QR should be shown
	 * Conditions:
	 * - Order is CLOSED
	 * - Order is not cancelled
	 * - Store has autofacturacion enabled (not DISABLED)
	 * - Store has default_billing_data_id
	 * - Price type allows bill code (not 'NO')
	 */
	private shouldShowBillCode(): boolean {
		const order = this.order_info?.order;
		const store = this.order_info?.store;
		const priceType = this.order_info?.price_type;

		return order?.status === 'CLOSED' &&
			!order?.cancellation_timestamp &&
			store?.autofacturacion_settings !== 'DISABLED' &&
			!!store?.default_billing_data_id &&
			priceType?.show_bill_code !== 'NO';
	}

	private shouldShowQrCode(): boolean {
		return this.shouldShowBillCode() &&
			this.order_info?.store?.show_facturacion_qr === 'YES' &&
			!!this.facturacion_url;
	}

	private getEncoderMaxColumns(font_name:'a' | 'b' = 'b'): number
	{
		return font_name == 'a' ? this.ticket_a_columns : this.ticket_b_columns;
	}

	public buildXml(): string {
		console.log('=== TicketBuilder Debug ===');
		console.log('ticket.id:', this.ticket?.id, 'ticket.name:', this.ticket?.name);
		console.log('ticket.font_b_columns:', this.ticket?.font_b_columns);
		console.log('this.ticket_b_columns:', this.ticket_b_columns);
		console.log('show_price_col:', this.show_price_col, 'item_price_display:', this.ticket?.item_price_display);
		console.log('show_qty_times_price_col:', this.show_qty_times_price_col);
		console.log('show_total_col:', this.show_total_col);

		if (this.calculation_style === 'SAT_FACTURA') {
			this.calculateFacturaColumnWidths();
		} else {
			this.calculateColumnWidths();
		}

		console.log('price_col_width:', this.price_col_width);
		console.log('qty_times_price_col_width:', this.qty_times_price_col_width);
		console.log('total_col_width:', this.total_col_width);
		console.log('=== End Debug ===');

		let xml = '<EscPos>\n';
		xml += '<Init></Init>\n';
		xml += this.buildHeader();
		if (this.calculation_style === 'SAT_FACTURA') {
			xml += this.buildFacturaItemsTable();
		} else {
			xml += this.buildItemsTable();
		}
		xml += this.buildFooter();
		xml += '</EscPos>';
		console.log('Generated XML:', xml);
		return xml;
	}

	public defaulticket():Ticket
	{
		return {
			id: 0,
			created: new Date(),
			created_by_user_id: 0,
			calculation_style: 'NORMAL',
			exceptions_display: 'ONLY_IF_PRESENT',
			font_a_columns: 42,
			font_b_columns: 56,
			footer: '',
			header: '',
			image_width: 128,
			item_money_sign_display: 'ONLY_IF_HAS_PRICE',
			item_name_display: 'TRUNCATED',
			item_note_display: 'ALWAYS',
			item_price_display:'ONLY_IF_HAS_PRICE',
			item_qty1_display: 'NEVER',
			item_qty2_display: 'NEVER',
			item_qty_times_price_display: 'ONLY_IF_HAS_PRICE',
			item_subtotal_display: 'NEVER',
			item_total_display: 'ONLY_IF_HAS_PRICE',
			name: '',
			option_item_display: 'ALWAYS',
			option_price_display: 'ONLY_IF_HAS_PRICE',
			option_qty_by_item: 'ALWAYS',
			option_row_display: 'ALWAYS',
			option_total_qty: 'ONLY_IF_HAS_PRICE',
			option_total_qty_times_price:'ONLY_IF_HAS_PRICE',
			order_note_display: 'ALWAYS',
			updated: new Date(),
			updated_by_user_id: 0,
		};
	}

	private buildCompactItemsList(): void {
		const itemMap = new Map<string, CompactItem>();

		for (const oii of this.order_info.items) {
			if (oii.order_item.type == 'REFUND') continue;

			const key = `${oii.item.name}_${oii.order_item.unitary_price}`;

			const existing = itemMap.get(key);
			if (existing) {
				existing.qty += oii.order_item.qty;
				existing.subtotal += oii.order_item.subtotal;
				existing.total += oii.order_item.total;
				existing.tax += oii.order_item.tax || 0;
			} else {
				itemMap.set(key, {
					item_name: oii.item.name,
					qty: oii.order_item.qty,
					unitary_price: oii.order_item.unitary_price,
					subtotal: oii.order_item.subtotal,
					total: oii.order_item.total,
					tax: oii.order_item.tax || 0
				});
			}
		}

		this.compact_items_list = Array.from(itemMap.values());
	}

	private calculateColumnWidths() {
		const items = this.compact_items ? this.compact_items_list : this.order_info.items;

		if (this.show_qty_col) {
			for (const item of items) {
				this.qty_col_width = Math.max(this.qty_col_width, this.getItemQty(item).toString().length);
			}
			this.qty_col_width += 1;
		}

		if (this.show_qty2_col) {
			for (const item of items) {
				this.qty2_col_width = Math.max(this.qty2_col_width, this.getItemQty(item).toString().length);
			}
			this.qty2_col_width += 1;
		}

		if (this.show_price_col)
		{
			for (const item of items)
			{
				this.price_col_width = Math.max(this.price_col_width, Utils.formatMoney(this.getItemPrice(item)).length);
			}
			this.price_col_width += 1;
		}

		if (this.show_qty_times_price_col) {
			for (const item of items) {
				const qty_times_price = this.getItemQty(item)+'x'+Utils.formatMoney(this.getItemPrice(item));
				this.qty_times_price_col_width = Math.max(this.qty_times_price_col_width, qty_times_price.length);
			}
			this.qty_times_price_col_width += 1;
		}

		if (this.show_subtotal_col)
		{
			for (const item of items) {
				this.subtotal_col_width = Math.max(this.subtotal_col_width, Utils.formatMoney(this.getItemSubtotal(item)).length);
			}
			this.subtotal_col_width += 1;
		}

		if (this.show_total_col) {
			for (const item of items) {
				this.total_col_width = Math.max(this.total_col_width, Utils.formatMoney(this.getItemTotal(item)).length);
			}
			this.total_col_width += 1;
		}
	}

	private getItemQty(item: any): number {
		return item.order_item ? item.order_item.qty : item.qty;
	}

	private getItemPrice(item: any): number {
		return item.order_item ? item.order_item.unitary_price : item.unitary_price;
	}

	private getItemSubtotal(item: any): number {
		return item.order_item ? item.order_item.subtotal : item.subtotal;
	}

	private getItemTotal(item: any): number {
		return item.order_item ? item.order_item.total : item.total;
	}

	private calculateFacturaValues(): void {
		this.factura_items = [];
		let total_importe = 0;
		let total_descuento = 0;
		let total_iva = 0;
		let total_ieps = 0;

		if (!this.order_info?.items) {
			return;
		}

		for (let oii of this.order_info.items) {
			if (!oii?.order_item || oii.order_item.type == 'REFUND') continue;
			if (!oii?.item) continue;

			const tax_percent = this.getItemTax(oii);
			const ieps_percent = oii.order_item.ieps_type == 'RATE' ? (oii.order_item.ieps_value || 0) : 0;
			const qty = oii.order_item.qty || 0;

			const base = oii.order_item.subtotal || 0;

			const valor_unitario_preciso = base / qty;
			const valor_unitario = Math.ceil(valor_unitario_preciso * 100) / 100;

			const importe = Math.round(valor_unitario * qty * 100) / 100;

			const rounding_error = Math.round((importe - base) * 100) / 100;

			const iva = oii.order_item.tax || 0;
			const ieps_value = ieps_percent > 0 ? Math.round(base * (ieps_percent / 100) * 100) / 100 : 0;

			const discount_percent = oii.order_item.discount_percent || 0;
			const discount = oii.order_item.discount || 0;

			let real_discount = 0;
			let has_discount = false;

			if (discount_percent > 0) {
				const original_price = oii.order_item.original_unitary_price || 0;
				const current_price = oii.order_item.unitary_price || 0;
				real_discount = Math.round((original_price - current_price) * qty * 100) / 100;
				has_discount = true;
			} else if (discount > 0) {
				real_discount = discount;
				has_discount = true;
			}

			const descuento = Math.round((rounding_error + real_discount) * 100) / 100;

			this.factura_items.push({
				item_name: oii.item.name,
				qty: qty,
				valor_unitario: valor_unitario,
				importe: importe,
				descuento: descuento,
				rounding_error: rounding_error,
				real_discount: real_discount,
				base: base,
				iva: iva,
				ieps: ieps_value,
				tax_percent: tax_percent,
				ieps_percent: ieps_percent,
				discount_percent: discount_percent,
				has_discount: has_discount,
				note: oii.order_item.note || undefined,
				exceptions: oii.order_item.exceptions || undefined,
				batch_option: oii.item.batch_option || 'NONE',
				batches: (oii.batches || []).map(b => ({ batch: b.batch || undefined, expiration_date: b.expiration_date || undefined }))
			});

			total_importe += importe;
			total_descuento += descuento;
			total_iva += iva;
			total_ieps += ieps_value;
		}

		this.factura_subtotal = Math.round(total_importe * 100) / 100;
		this.factura_descuento = Math.round(total_descuento * 100) / 100;
		this.factura_subtotal_neto = Math.round((total_importe - total_descuento) * 100) / 100;
		this.factura_iva = Math.round(total_iva * 100) / 100;
		this.factura_ieps = Math.round(total_ieps * 100) / 100;
		this.factura_total = Math.round((this.order_info.order.total - (this.order_info.order.discount || 0)) * 100) / 100;
	}

	private getItemTax(ii: OrderItemInfo): number {
		return ii.item.applicable_tax == 'DEFAULT'
			? this.order_info.order.tax_percent
			: ii.item.tax_percent;
	}

	private calculateFacturaColumnWidths() {
		if (this.show_qty_col) {
			for (const item of this.factura_items) {
				this.qty_col_width = Math.max(this.qty_col_width, item.qty.toString().length);
			}
			if (this.qty_col_width > 0) this.qty_col_width += 1;
		}

		if (this.show_price_col) {
			for (const item of this.factura_items) {
				this.valor_unitario_col_width = Math.max(this.valor_unitario_col_width, Utils.formatMoney(item.valor_unitario).length);
			}
			if (this.valor_unitario_col_width > 0) this.valor_unitario_col_width += 1;
		}

		if (this.show_subtotal_col) {
			for (const item of this.factura_items) {
				this.importe_col_width = Math.max(this.importe_col_width, Utils.formatMoney(item.importe).length);
			}
			if (this.importe_col_width > 0) this.importe_col_width += 1;
		}

		if (this.show_total_col) {
			for (const item of this.factura_items) {
				this.total_col_width = Math.max(this.total_col_width, Utils.formatMoney(item.base + item.iva + item.ieps).length);
			}
			if (this.total_col_width > 0) this.total_col_width += 1;
		}

		const hasAnyDiscount = this.factura_items.some(item => item.real_discount > 0);
		if (hasAnyDiscount) {
			for (const item of this.factura_items) {
				if (item.real_discount > 0) {
					this.descuento_col_width = Math.max(this.descuento_col_width, Utils.formatMoney(item.real_discount).length);
				}
			}
			if (this.descuento_col_width > 0) {
				this.descuento_col_width += 1;
			}
		}
	}

	private buildHeader(): string {
		let header = '';

		if (this.ticket.header) {
			if (this.ticket.header.includes('<') && this.ticket.header.includes('</')) {
				header += this.sanitizeXml(this.ticket.header) + '\n';
			} else if (this.ticket.header.includes('<')) {
				console.warn('Skipping malformed header XML:', this.ticket.header);
			} else {
				header += `<Text justification="center">${this.escapeXml(this.ticket.header)}</Text>\n`;
			}
		}

		header += this.buildOrderInfo();

		const serviceType = this.order_info.order.service_type;
		if (serviceType && serviceType !== 'IN_PLACE' && serviceType !== 'QUICK_SALE') {
			const serviceLabels: Record<string, string> = {
				'TOGO': 'A DOMICILIO',
				'PICK_UP': 'PARA LLEVAR'
			};
			const serviceLabel = serviceLabels[serviceType] || serviceType;
			header += `<Text justification="center" bold="true">${serviceLabel}</Text>\n`;
		}

		if (this.ticket.order_note_display === 'ALWAYS' && this.order_info.order.note) {
			header += `<Text justification="left">NOTA: ${this.escapeXml(this.order_info.order.note)}</Text>\n`;
		}

		return header;
	}

	private buildOrderInfo(): string {
		let xml = '';
		const order = this.order_info.order;
		const maxCols = this.getEncoderMaxColumns('b');

		xml += `<Text font="b" bold="true">ID:</Text><Text font="b"> ${order.id}</Text><Feed lines="1"></Feed>\n`;
		if (order.store_consecutive) {
			xml += `<Text font="b" bold="true">Folio:</Text><Text font="b"> #${order.store_consecutive}</Text><Feed lines="1"></Feed>\n`;
		}
		if (order.created) {
			const date = new Date(order.created);
			const dateStr = date.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
			const timeStr = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
			xml += `<Text font="b" bold="true">Fecha: ${dateStr} ${timeStr}</Text><Feed lines="1"></Feed>\n`;
		}
		if (order.client_name) {
			xml += `<Text font="b" bold="true">Cliente: ${this.escapeXml(order.client_name.toUpperCase())}</Text><Feed lines="1"></Feed>\n`;
		}
		if (this.order_info.cashier?.name) {
			xml += `<Text font="b" bold="true">Atendio: ${this.escapeXml(this.order_info.cashier.name.toUpperCase())}</Text><Feed lines="1"></Feed>\n`;
		}

		return xml;
	}

	private buildItemsTable(): string {
		let tableXml = '';
		tableXml += `<Table font="${this.font_row}">\n`;
		tableXml += this.buildItemsTableColumns();
		tableXml += this.buildItemsTableRows();
		tableXml += '</Table>\n';
		return tableXml;
	}

	private buildFacturaItemsTable(): string {
		let tableXml = '';
		tableXml += `<Table font="${this.font_row}">\n`;
		tableXml += this.buildFacturaItemsTableColumns();
		tableXml += this.buildFacturaItemsTableRows();
		tableXml += '</Table>\n';
		return tableXml;
	}

	private buildFacturaItemsTableColumns(): string {
		let columnsXml = '<Columns>\n';
		let remaining_width = this.getEncoderMaxColumns(this.font_row as 'a' | 'b');

		if (this.show_qty_col) {
			columnsXml += `<Column width="${this.qty_col_width}" align="left" marginRight="1"></Column>\n`;
			remaining_width -= this.qty_col_width + 1;
		}

		let item_name_width = remaining_width;

		if (this.show_price_col) {
			item_name_width -= this.valor_unitario_col_width + 1;
		}

		if (this.show_subtotal_col) {
			item_name_width -= this.importe_col_width + 1;
		}

		if (this.descuento_col_width > 0) {
			item_name_width -= this.descuento_col_width + 1;
		}

		if (this.show_total_col) {
			item_name_width -= this.total_col_width;
		}

		// Subtract 1 for item_name's own marginRight
		item_name_width = Math.max(1, item_name_width - 1);

		columnsXml += `<Column width="${item_name_width}" align="left" marginRight="1"></Column>\n`;

		if (this.show_price_col) {
			columnsXml += `<Column width="${this.valor_unitario_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.show_subtotal_col) {
			columnsXml += `<Column width="${this.importe_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.descuento_col_width > 0) {
			columnsXml += `<Column width="${this.descuento_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.show_total_col) {
			columnsXml += `<Column width="${this.total_col_width}" align="right"></Column>\n`;
		}

		columnsXml += '</Columns>\n';
		return columnsXml;
	}

	private buildFacturaItemsTableRows(): string {
		let rowsXml = '<Rows>\n';
		rowsXml += this.buildFacturaHeaderRow();

		for (const item of this.factura_items) {
			rowsXml += this.buildFacturaItemRow(item);
		}
		rowsXml += '</Rows>\n';
		return rowsXml;
	}

	private buildFacturaHeaderRow(): string {
		let rowXml = '<Row>\n';
		if (this.show_qty_col) {
			rowXml += '<Cell><Text>Cant</Text></Cell>\n';
		}
		rowXml += '<Cell><Text>Articulo</Text></Cell>\n';

		if (this.show_price_col) {
			rowXml += '<Cell><Text>P.U.</Text></Cell>\n';
		}
		if (this.show_subtotal_col) {
			rowXml += '<Cell><Text>Importe</Text></Cell>\n';
		}
		if (this.descuento_col_width > 0) {
			rowXml += '<Cell><Text>Desc</Text></Cell>\n';
		}
		if (this.show_total_col) {
			rowXml += '<Cell><Text>Total</Text></Cell>\n';
		}
		rowXml += '</Row>\n';
		return rowXml;
	}

	private buildFacturaItemRow(item: FacturaItem): string {
		const qty = item.qty.toString();
		const name = item.item_name;
		const valor_unitario = Utils.formatMoney(item.valor_unitario);
		const importe = Utils.formatMoney(item.importe);
		const descuento = item.real_discount > 0 ? Utils.formatMoney(item.real_discount) : '';
		const total = Utils.formatMoney(item.base + item.iva + item.ieps);

		let rowXml = '<Row>\n';
		if (this.show_qty_col) {
			rowXml += `<Cell><Text font="${this.font_row}">${qty}</Text></Cell>\n`;
		}

		let name_str = this.escapeXml(name);
		rowXml += `<Cell><Text>${name_str}</Text></Cell>\n`;

		if (this.show_price_col) {
			rowXml += `<Cell><Text>${valor_unitario}</Text></Cell>\n`;
		}
		if (this.show_subtotal_col) {
			rowXml += `<Cell><Text>${importe}</Text></Cell>\n`;
		}
		if (this.descuento_col_width > 0) {
			rowXml += `<Cell><Text>${descuento}</Text></Cell>\n`;
		}
		if (this.show_total_col) {
			rowXml += `<Cell><Text>${total}</Text></Cell>\n`;
		}
		rowXml += '</Row>\n';

		if (item.has_discount) {
			if (item.discount_percent && item.discount_percent > 0) {
				rowXml += this.buildInfoRow('Ahorro', item.discount_percent + '% (-$' + Utils.formatMoney(item.real_discount) + ')');
			} else if (item.real_discount > 0) {
				rowXml += this.buildInfoRow('Ahorro', '$' + Utils.formatMoney(item.real_discount));
			}
		}

		if (item.note) {
			rowXml += this.buildInfoRow('Nota', item.note);
		}
		if (item.exceptions) {
			rowXml += this.buildInfoRow('Excepcion', item.exceptions);
		}

		for (const batch of item.batches) {
			if ((item.batch_option === 'BATCH_ONLY' || item.batch_option === 'BATCH_AND_EXPIRATION') && batch.batch) {
				rowXml += this.buildInfoRow('Lote', batch.batch);
			}
			if ((item.batch_option === 'EXPIRATION_ONLY' || item.batch_option === 'BATCH_AND_EXPIRATION') && batch.expiration_date) {
				rowXml += this.buildInfoRow('Caducidad', Utils.getDateString(batch.expiration_date, false));
			}
		}

		return rowXml;
	}

	private buildInfoRow(label: string, value: string): string {
		const maxCols = this.getEncoderMaxColumns(this.font_row as 'a' | 'b');
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 4));
		return `<Text font="${this.font_row}" justification="right">${label}:${dots}${value}</Text>\n`;
	}

	private buildItemsTableColumns(): string {
		let columnsXml = '<Columns>\n';

		let remaining_width = this.getEncoderMaxColumns(this.font_row as 'a' | 'b');
		console.log('buildItemsTableColumns - remaining_width:', remaining_width, 'font_row:', this.font_row);

		if (this.show_qty_col) {
			columnsXml += `<Column width="${this.qty_col_width}" align="left" marginRight="1"></Column>\n`;
			remaining_width -= this.qty_col_width + 1;
		}

		let item_name_width = remaining_width;

		if (this.show_qty2_col) {
			item_name_width -= this.qty2_col_width + 1;
		}

		if (this.show_price_col) {
			item_name_width -= this.price_col_width + 1;
		}

		if (this.show_qty_times_price_col) {
			item_name_width -= this.qty_times_price_col_width + 1;
		}

		if (this.show_subtotal_col) {
			item_name_width -= this.subtotal_col_width + 1;
		}

		if (this.show_total_col) {
			item_name_width -= this.total_col_width;
		}

		// Subtract 1 for item_name's own marginRight
		item_name_width = Math.max(1, item_name_width - 1);

		columnsXml += `<Column width="${item_name_width}" align="left" marginRight="1"></Column>\n`;

		if (this.show_qty2_col) {
			columnsXml += `<Column width="${this.qty2_col_width}" align="left" marginRight="1"></Column>\n`;
		}

		if (this.show_price_col) {
			columnsXml += `<Column width="${this.price_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.show_qty_times_price_col) {
			columnsXml += `<Column width="${this.qty_times_price_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.show_subtotal_col) {
			columnsXml += `<Column width="${this.subtotal_col_width}" align="right" marginRight="1"></Column>\n`;
		}

		if (this.show_total_col) {
			columnsXml += `<Column width="${this.total_col_width}" align="right"></Column>\n`;
		}

		columnsXml += '</Columns>\n';
		return columnsXml;
	}

	private buildHeaderRow(): string {
		let rowXml = '<Row>\n';
		if (this.show_qty_col) {
			rowXml += '<Cell><Text>Cant</Text></Cell>\n';
		}
		rowXml += '<Cell><Text>Articulo</Text></Cell>\n';

		if (this.show_qty2_col) {
			rowXml += '<Cell><Text>Cant</Text></Cell>\n';
		}
		if (this.show_price_col) {
			rowXml += '<Cell><Text>$</Text></Cell>\n';
		}
		if (this.show_qty_times_price_col) {
			rowXml += '<Cell><Text>CantxPrec</Text></Cell>';
		}
		if (this.show_subtotal_col) {
			rowXml += '<Cell><Text>Sub</Text></Cell>\n';
		}
		if (this.show_total_col) {
			rowXml += '<Cell><Text>Total</Text></Cell>\n';
		}
		rowXml += '</Row>\n';
		return rowXml;
	}

	private buildItemsTableRows(): string {
		let rowsXml = '<Rows>\n';
		rowsXml += this.buildHeaderRow();

		if (this.compact_items) {
			for (const item of this.compact_items_list) {
				rowsXml += this.buildCompactItemRow(item);
			}
		} else {
			for (const item of this.order_info.items) {
				rowsXml += this.buildItemRow(item);
				if (item.order_item.discount_percent && item.order_item.discount_percent > 0) {
					rowsXml += this.buildInfoRow('Descuento', item.order_item.discount_percent + '%');
				}
				if (item.order_item.note) {
					rowsXml += this.buildInfoRow('Nota', item.order_item.note);
				}
				if (item.order_item.exceptions) {
					rowsXml += this.buildInfoRow('Excepcion', item.order_item.exceptions);
				}

				for (const batch of (item.batches || [])) {
					if ((item.item.batch_option === 'BATCH_ONLY' || item.item.batch_option === 'BATCH_AND_EXPIRATION') && batch.batch) {
						rowsXml += this.buildInfoRow('Lote', batch.batch);
					}
					if ((item.item.batch_option === 'EXPIRATION_ONLY' || item.item.batch_option === 'BATCH_AND_EXPIRATION') && batch.expiration_date) {
						rowsXml += this.buildInfoRow('Caducidad', Utils.getDateString(batch.expiration_date, false));
					}
				}

				if (this.show_options && item.options && item.options.length > 0) {
					rowsXml += this.buildItemOptionRows(item);
				}
				if (item.order_item.has_separator === 'YES') {
					rowsXml += this.buildSeparatorRow();
				}
			}
		}
		rowsXml += '</Rows>\n';
		return rowsXml;
	}

	private buildItemOptionRows(item: OrderItemInfo): string {
		let rowsXml = '';

		if (!item.options || item.options.length === 0) {
			return rowsXml;
		}

		const parentQty = item.order_item.qty || 1;
		console.log('=== buildItemOptionRows DEBUG ===');
		console.log('Parent item:', item.item.name, 'Parent qty:', parentQty);

		for (const option of item.options) {
			// Option group name
			const optionGroupName = option.item_option?.name || '';

			// Build rows for each selected value in this option
			if (option.values && option.values.length > 0) {
				for (const value of option.values) {
					// Get qty information - the backend adds qty to the value object
					const totalQty = (value as any).qty || 0;
					const qtyPerItem = totalQty > 0 ? totalQty / parentQty : 0;

					console.log('Option value:', value.item?.name || optionGroupName);
					console.log('  totalQty:', totalQty);
					console.log('  qtyPerItem:', qtyPerItem);
					console.log('  value object:', value);
					console.log('  ticket.option_qty_by_item:', this.ticket.option_qty_by_item);

					// Format qtyPerItem: if it's a whole number, show as integer, otherwise show with decimals
					const qtyPerItemStr = qtyPerItem % 1 === 0 ? qtyPerItem.toFixed(0) : qtyPerItem.toFixed(2);

					rowsXml += '<Row>\n';

					// Show total qty in qty column based on preference
					if (this.show_qty_col) {
						const showQty = this.ticket.option_total_qty === 'ALWAYS' ||
							(this.ticket.option_total_qty === 'ONLY_IF_HAS_PRICE' && (value.item_option_value?.price || 0) > 0);
						const qtyText = (showQty && totalQty > 0) ? totalQty.toString() : '';
						rowsXml += `<Cell><Text>${qtyText}</Text></Cell>\n`;
					}

					// Build option value name with qty per item prefix if enabled
					let valueName = value.item?.name || optionGroupName;
					const showQtyByItem = this.ticket.option_qty_by_item === 'ALWAYS' ||
						(this.ticket.option_qty_by_item === 'ONLY_IF_HAS_PRICE' && (value.item_option_value?.price || 0) > 0);

					console.log('  showQtyByItem:', showQtyByItem);

					if (showQtyByItem && qtyPerItem > 0) {
						valueName = `  ${qtyPerItemStr}- ${valueName}`;
						console.log('  Final valueName (with qty):', valueName);
					} else {
						valueName = `  + ${valueName}`;
						console.log('  Final valueName (without qty):', valueName);
					}

					rowsXml += `<Cell><Text>${this.escapeXml(valueName)}</Text></Cell>\n`;

					// Empty or qty2 cell
					if (this.show_qty2_col) {
						const showQty2 = this.ticket.option_total_qty === 'ALWAYS' ||
							(this.ticket.option_total_qty === 'ONLY_IF_HAS_PRICE' && (value.item_option_value?.price || 0) > 0);
						const qtyText = (showQty2 && totalQty > 0) ? totalQty.toString() : '';
						rowsXml += `<Cell><Text>${qtyText}</Text></Cell>\n`;
					}

					if (this.show_price_col) {
						const price = value.item_option_value?.price || value.item_option_value?.extra_price || 0;
						rowsXml += `<Cell><Text>${price > 0 ? '$' + price.toFixed(2) : ''}</Text></Cell>\n`;
					}

					if (this.show_qty_times_price_col) {
						const showQtyTimesPrice = this.ticket.option_total_qty_times_price === 'ALWAYS' ||
							(this.ticket.option_total_qty_times_price === 'ONLY_IF_HAS_PRICE' && (value.item_option_value?.price || 0) > 0);
						let qtyTimesPriceText = '';
						if (showQtyTimesPrice && totalQty > 0) {
							const price = value.item_option_value?.price || value.item_option_value?.extra_price || 0;
							if (price > 0) {
								qtyTimesPriceText = `${totalQty}x${Utils.formatMoney(price)}`;
							}
						}
						rowsXml += `<Cell><Text>${qtyTimesPriceText}</Text></Cell>\n`;
					}

					if (this.show_subtotal_col) {
						rowsXml += '<Cell><Text></Text></Cell>\n';
					}

					if (this.show_total_col) {
						const showTotal = this.ticket.option_price_display === 'ALWAYS' ||
							(this.ticket.option_price_display === 'ONLY_IF_HAS_PRICE' && (value.item_option_value?.price || 0) > 0);
						let totalText = '';
						if (showTotal) {
							const price = value.item_option_value?.price || 0;
							const total = price * totalQty;
							totalText = total > 0 ? '$' + total.toFixed(2) : '';
						}
						rowsXml += `<Cell><Text>${totalText}</Text></Cell>\n`;
					}

					rowsXml += '</Row>\n';
				}
			}
		}

		return rowsXml;
	}

	private buildSeparatorRow(): string {
		let rowXml = '<Row>\n';
		if (this.show_qty_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		rowXml += '<Cell><Rule></Rule></Cell>\n';
		if (this.show_qty2_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		if (this.show_price_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		if (this.show_qty_times_price_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		if (this.show_subtotal_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		if (this.show_total_col) {
			rowXml += '<Cell><Rule></Rule></Cell>\n';
		}
		rowXml += '</Row>\n';
		return rowXml;
	}

	private buildRow(qty: string, name: string, qty2: string, price: string, qty_times_price: string, subtotal: string, total: string): string {
		let rowXml = '<Row>\n';
		if (this.show_qty_col) {
			rowXml += `<Cell><Text font="${this.font_row}">${qty}</Text></Cell>\n`;
		}

		let name_str = this.escapeXml(name);

		rowXml += `<Cell><Text>${name_str}</Text></Cell>\n`;
		if (this.show_qty2_col) {
			rowXml += `<Cell><Text>${qty2}</Text></Cell>\n`;
		}
		if (this.show_price_col) {
			rowXml += `<Cell><Text>${price}</Text></Cell>\n`;
		}
		if (this.show_qty_times_price_col) {
			rowXml += `<Cell><Text>${qty_times_price}</Text></Cell>\n`;
		}
		if (this.show_subtotal_col) {
			rowXml += `<Cell><Text>${subtotal}</Text></Cell>\n`;
		}
		if (this.show_total_col) {
			rowXml += `<Cell><Text>${total}</Text></Cell>\n`;
		}
		rowXml += '</Row>\n';
		return rowXml;
	}

	private buildItemRow(order_item_info: OrderItemInfo): string {
		const isOptionItem = order_item_info.order_item.item_option_id != null;

		let qty = order_item_info.order_item.qty.toString();
		const qty2 = this.show_qty2_col ? qty : '';
		let name = order_item_info.item.name;

		// Handle option items differently
		if (isOptionItem) {
			const itemOptionQty = order_item_info.order_item.item_option_qty || 0;
			const totalQty = order_item_info.order_item.qty || 0;

			console.log('=== OPTION ITEM ===');
			console.log('Item:', name);
			console.log('item_option_qty:', itemOptionQty);
			console.log('total qty:', totalQty);
			console.log('ticket.option_qty_by_item:', this.ticket.option_qty_by_item);

			// Show qty per item as prefix based on preference
			const showQtyByItem = this.ticket.option_qty_by_item === 'ALWAYS' ||
				(this.ticket.option_qty_by_item === 'ONLY_IF_HAS_PRICE' && (order_item_info.order_item.unitary_price || 0) > 0);

			if (showQtyByItem && itemOptionQty > 0) {
				const qtyStr = itemOptionQty % 1 === 0 ? itemOptionQty.toFixed(0) : itemOptionQty.toFixed(2);
				name = `  ${qtyStr}- ${name}`;
				console.log('Added prefix, new name:', name);
			} else {
				name = `  + ${name}`;
			}

			// Override qty display for option items based on preference
			const showTotalQty = this.ticket.option_total_qty === 'ALWAYS' ||
				(this.ticket.option_total_qty === 'ONLY_IF_HAS_PRICE' && (order_item_info.order_item.unitary_price || 0) > 0);
			if (!showTotalQty) {
				qty = '';
			}
		}

		let price: string;
		let qty_times_price: string;
		let total: string;

		if (this.calculation_style === 'NORMAL') {
			const totalWithTax = order_item_info.order_item.total || 0;
			const unitPriceWithTax = order_item_info.order_item.qty !== 0 ? totalWithTax / order_item_info.order_item.qty : 0;
			price = Utils.formatMoney(unitPriceWithTax);
			qty_times_price = order_item_info.order_item.qty + 'x' + Utils.formatMoney(unitPriceWithTax);
			total = totalWithTax > 0 ? Utils.formatMoney(totalWithTax) : '';
		} else {
			price = Utils.formatMoney(order_item_info.order_item.unitary_price);
			qty_times_price = order_item_info.order_item.qty + 'x' + Utils.formatMoney(order_item_info.order_item.unitary_price);
			total = order_item_info.order_item.total > 0 ? Utils.formatMoney(order_item_info.order_item.total) : '';
		}

		// For option items, apply display preferences to price/total columns
		if (isOptionItem) {
			const showPrice = this.ticket.option_price_display === 'ALWAYS' ||
				(this.ticket.option_price_display === 'ONLY_IF_HAS_PRICE' && (order_item_info.order_item.unitary_price || 0) > 0);
			if (!showPrice) {
				total = '';
			}

			const showQtyTimesPrice = this.ticket.option_total_qty_times_price === 'ALWAYS' ||
				(this.ticket.option_total_qty_times_price === 'ONLY_IF_HAS_PRICE' && (order_item_info.order_item.unitary_price || 0) > 0);
			if (!showQtyTimesPrice) {
				qty_times_price = '';
			}
		}

		const subtotal = Utils.formatMoney(order_item_info.order_item.subtotal);
		return this.buildRow(qty, name, qty2, price, qty_times_price, subtotal, total);
	}

	private buildCompactItemRow(item: CompactItem): string {
		const qty = item.qty.toString();
		const qty2 = this.show_qty2_col ? qty : '';
		const name = item.item_name;

		let price: string;
		let qty_times_price: string;
		let total: string;

		if (this.calculation_style === 'NORMAL') {
			const totalWithTax = item.total || 0;
			const unitPriceWithTax = qty !== '0' ? totalWithTax / item.qty : 0;
			price = Utils.formatMoney(unitPriceWithTax);
			qty_times_price = item.qty + 'x' + Utils.formatMoney(unitPriceWithTax);
			total = totalWithTax > 0 ? Utils.formatMoney(totalWithTax) : '';
		} else {
			price = Utils.formatMoney(item.unitary_price);
			qty_times_price = item.qty + 'x' + Utils.formatMoney(item.unitary_price);
			total = item.total > 0 ? Utils.formatMoney(item.total) : '';
		}

		const subtotal = Utils.formatMoney(item.subtotal);
		return this.buildRow(qty, name, qty2, price, qty_times_price, subtotal, total);
	}

	private buildFooter(): string {
		let footer = '';

		if (this.calculation_style === 'SAT_FACTURA') {
			footer += this.buildFacturaTotals();
		} else {
			footer += this.buildNormalTotals();
		}

		footer += this.buildPaymentInfo();

		if (this.ticket.footer) {
			// Only include raw XML if it appears to be valid (has matching closing tags)
			// Otherwise escape it as text
			if (this.ticket.footer.includes('<') && this.ticket.footer.includes('</')) {
				footer += this.sanitizeXml(this.ticket.footer) + '\n';
			} else if (this.ticket.footer.includes('<')) {
				// Has opening tag but no closing - skip malformed XML
				console.warn('Skipping malformed footer XML:', this.ticket.footer);
			} else {
				footer += `<Text justification="center">${this.escapeXml(this.ticket.footer)}</Text>\n`;
			}
		}

		// Add facturacion code and QR code if applicable
		footer += this.buildFacturacionSection();

		return footer;
	}

	/**
	 * Build the facturacion code and QR code section
	 */
	private buildFacturacionSection(): string {
		let xml = '';

		// Show facturacion code if conditions are met
		if (this.shouldShowBillCode() && this.order_info.order.facturacion_code) {
			xml += '<Feed lines="1"></Feed>\n';
			xml += '<Text justification="center">Codigo Facturacion:</Text>\n';
			xml += '<Feed lines="1"></Feed>\n';
			xml += `<Text justification="center" fontWidth="2" fontHeight="2" bold="true">${this.escapeXml(this.order_info.order.facturacion_code.toUpperCase())}</Text>\n`;
			xml += '<Feed lines="1"></Feed>\n';
		}

		// Show QR code if conditions are met
		if (this.shouldShowQrCode()) {
			xml += `<QrCode justification="center" size="${this.qr_size}">${this.escapeXml(this.facturacion_url)}</QrCode>\n`;
			xml += '<Feed lines="1"></Feed>\n';
		}

		// Final cut
		xml += '<Feed lines="2"></Feed>\n';
		xml += '<Cut mode="partial"></Cut>\n';

		return xml;
	}

	private buildNormalTotals(): string {
		let xml = '';
		const maxCols = this.getEncoderMaxColumns('a');
		console.log('buildNormalTotals - maxCols (font a):', maxCols, 'ticket_a_columns:', this.ticket_a_columns);

		xml += '<Feed lines="1"></Feed>\n';

		const orderDiscount = this.order_info?.order?.discount || 0;
		if (orderDiscount > 0) {
			const subtotal = (this.order_info?.items || []).reduce((sum, item) => {
				if (item?.order_item?.type == 'REFUND') return sum;
				return sum + (item?.order_item?.total || 0);
			}, 0);
			xml += this.buildTotalsRow('Subtotal', Utils.formatMoney(subtotal), maxCols);
			xml += this.buildTotalsRow('Descuento', '-' + Utils.formatMoney(orderDiscount), maxCols);
		}

		xml += '<Feed lines="1"></Feed>\n';

		const orderTotal = this.order_info?.order?.total || 0;
		const discount = this.order_info?.order?.discount || 0;
		const total = orderTotal - discount;
		xml += this.buildTotalsRow('TOTAL', Utils.formatMoney(total), maxCols, true);

		xml += '<Feed lines="2"></Feed>\n';

		return xml;
	}

	private buildFacturaTotals(): string {
		let xml = '';
		const maxCols = this.getEncoderMaxColumns('a');

		xml += '<Feed lines="1"></Feed>\n';

		xml += this.buildTotalsRow('Subtotal', Utils.formatMoney(this.factura_subtotal || 0), maxCols);

		const realDiscount = this.calculateTotalRealDiscount();
		if (realDiscount > 0) {
			xml += this.buildTotalsRow('Descuento', '-' + Utils.formatMoney(realDiscount), maxCols);
		}

		const totalRoundingError = this.calculateTotalRoundingError();
		if (totalRoundingError > 0) {
			xml += this.buildTotalsRow('Desc. por redondeo', '-' + Utils.formatMoney(totalRoundingError), maxCols);
		}

		const taxPercent = this.order_info?.order?.tax_percent || 0;
		xml += this.buildTotalsRow(`IVA (${taxPercent}%)`, Utils.formatMoney(this.factura_iva || 0), maxCols);

		if (this.factura_ieps > 0) {
			xml += this.buildTotalsRow('IEPS', Utils.formatMoney(this.factura_ieps), maxCols);
		}

		xml += '<Feed lines="1"></Feed>\n';
		xml += this.buildTotalsRow('TOTAL', Utils.formatMoney(this.factura_total || 0), maxCols, true);
		xml += '<Feed lines="2"></Feed>\n';

		return xml;
	}

	private calculateTotalRealDiscount(): number {
		let totalRealDiscount = 0;
		for (const item of this.factura_items) {
			totalRealDiscount += item.real_discount;
		}
		return Math.round(totalRealDiscount * 100) / 100;
	}

	private calculateTotalRoundingError(): number {
		let totalRoundingError = 0;
		for (const item of this.factura_items) {
			totalRoundingError += item.rounding_error;
		}
		return Math.round(totalRoundingError * 100) / 100;
	}

	private buildTotalsRow(label: string, value: string, maxCols: number, bold: boolean = false): string {
		const dots = '.'.repeat(Math.max(0, maxCols - label.length - value.length - 2));

		if (bold) {
			return `<Text font="a" bold="true">${label} ${dots} ${value}</Text><Feed lines="1"></Feed>\n`;
		}
		return `<Text font="a">${label} ${dots} ${value}</Text><Feed lines="1"></Feed>\n`;
	}

	private buildPaymentInfo(): string {
		if (!this.payment_info?.movements) {
			return '';
		}

		let xml = '';
		const maxCols = this.getEncoderMaxColumns('a');
		console.log('buildPaymentInfo - maxCols (font a):', maxCols, 'ticket_a_columns:', this.ticket_a_columns);

		xml += '<Feed lines="1"></Feed>\n';

		const legend: Record<string, string> = {
			'CASH-MXN': 'Efectivo',
			'CASH-USD': 'Dolares',
			'CREDIT_CARD-MXN': 'T. Credito',
			'DEBIT_CARD-MXN': 'T. Debito',
			'CHECK-MXN': 'Cheque',
			'TRANSFER-MXN': 'Transferencia',
			'DIGITAL_WALLET-MXN': 'Monedero',
		};

		for (const bmi of this.payment_info.movements) {
			for (const bmo of bmi.bank_movement_orders) {
				const paymentType = legend[bmi.bank_movement.transaction_type + '-' + bmo.currency_id] || bmi.bank_movement.transaction_type;
				let amount = bmo.currency_id !== 'MXN'
					? (bmi.bank_movement.amount_received || 0) * (bmo.exchange_rate || 1)
					: bmo.amount;
				xml += this.buildTotalsRow(paymentType, Utils.formatMoney(amount), maxCols);
			}
		}

		if (this.payment_info.payment?.received_amount != null && this.payment_info.payment.received_amount > 0) {
			xml += this.buildTotalsRow('Recibido', Utils.formatMoney(this.payment_info.payment.received_amount), maxCols);
		}

		if (this.payment_info.payment?.change_amount != null && this.payment_info.payment.change_amount >= 0) {
			xml += this.buildTotalsRow('Cambio', Utils.formatMoney(this.payment_info.payment.change_amount), maxCols);
		}

		return xml;
	}
}
